<template>
  <button class="Pressable" @click="handleClick" :class="{ ...props, evenPadding, ...computedClasses }">
    <slot v-if="hasStart" name="start"><iconify-icon :icon="props?.startIcon" /></slot>

    <slot></slot>

    <iconify-icon icon="svg-spinners:ring-resize" v-if="loading" />
    <slot v-if="hasEnd" name="end"><iconify-icon :icon="props?.endIcon" /></slot>
  </button>
</template>

<script setup lang="ts">
  import { computed, useSlots } from 'vue';

  const emit = defineEmits(['click', 'force-click']);

  const slots = useSlots();

  interface Props {
    startIcon?: string;
    endIcon?: string;
    disabled?: boolean;
    loading?: boolean;
    // size
    compact?: boolean;
    large?: boolean;
    extended?: boolean;
    rounded?: boolean;
    // color
    light?: boolean;
    danger?: boolean;
    outlined?: boolean;
    ghost?: boolean;
    black?: boolean;
    class?: string;
  }

  const props = withDefaults(defineProps<Props>(), { class: '' });

  // computed properties
  const hasStart = computed(() => !!props.startIcon || !!slots?.start);
  const hasEnd = computed(() => !!props.endIcon || !!slots?.end);
  const evenPadding = computed(() => hasStart.value || hasEnd.value);
  const computedClasses = computed(() => {
    const classes: any = {};
    for (let i of props.class.split(' ')) {
      if (i) classes[i] = true;
    }
    if (!props.danger && !props.ghost) classes['primary'] = true;
    return classes;
  });

  //functions
  function handleClick() {
    if (props.loading) return;
    if (props.disabled) {
      emit('force-click');
      return;
    }
    emit('click');
  }
</script>

<style scoped lang="scss">
  @import '@/assets/styles/variables';

  .Pressable {
    // layout
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    height: 2.5rem;
    padding: 0.5rem 2rem;

    // ui
    border-radius: 0.5rem;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 500;

    &.evenPadding {
      padding: 0.5rem 1rem;
    }

    // size
    &.compact {
      height: fit-content;
      padding: 0.25rem 0.75rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
    }
    &.large {
      height: 3.5rem;
      min-width: 20.375rem;
      font-size: 1rem;
    }
    &.extended {
      width: 100%;
    }
    &.rounded {
      border-radius: 1000px;
    }

    // color
    &.primary {
      background-color: $var-primary-main;
      color: $white;
    }

    &.danger {
      background-color: $communication-danger;
      color: $white;
    }
    &.light {
      background-color: $bg-dark;
      color: $neutral-black;
      box-shadow: none;
      &.danger {
        color: $communication-danger;
      }
    }
    &.black {
      background-color: $neutral-black;
      color: $white;
    }

    &.outlined {
      background-color: transparent;
      color: $var-primary-main;
      border: 1px solid $var-primary-main;
      &.danger {
        color: $communication-danger;
        border-color: $communication-danger;
      }
      box-shadow: none;
    }

    &.disabled {
      opacity: 0.8;
      cursor: not-allowed;
      &.danger {
        opacity: 0.6;
      }
    }

    &.ghost {
      background-color: transparent;
      transition: all ease 0.6;
    }
  }
</style>
